import { AddonSlideShowData } from '@/components/addons/slideshow/Data';
import AddonModel from '@/models/grid/AddonModel';
import { AddonModelState } from '@/typings/interfaces/state/Addon';
import { generateUniqueId, shuffle } from '@/utilities/Utilities';
import {
  SlideShowImageLinkType,
  SlideshowImageTarget,
  SlideshowTransition,
  SlideshowType
} from '@/components/addons/slideshow/enums';

export interface SliderItem {
  id?: string;
  image?: string;
  link?: {
    enabled?: boolean;
    type?: SlideShowImageLinkType;
    url?: string;
    section?: string;
    popover?: string;
    target?: SlideshowImageTarget;
  };
  width?: number;
  height?: number;
}

interface AddonSlideShowState extends AddonModelState {
  key: string;
  isReady: boolean;
  general: {
    images?: number;
    disableSwipe?: boolean;
    transition?: SlideshowTransition;
    transitionTime?: number;
    type?: SlideshowType;
    interval?: {
      enabled?: boolean;
      time?: number;
    };
    shuffle?: boolean;
    navigation?: {
      enabled?: boolean;
      arrowButtons?: boolean;
      bullets?: boolean;
    };
  };
  images?: SliderItem[];
}

export class AddonSlideShowModel extends AddonModel<AddonSlideShowData, AddonSlideShowState> {
  parseAddon(data: AddonSlideShowData) {
    const state = this.state;
    state.isReady = false;
    state.general = state.general ?? {};
    if (data.settings.general?.disable_swipe) {
      state.general.disableSwipe = data.settings.general?.disable_swipe === '1';
    }
    if (data.settings.general?.interval && data.settings.general?.interval.enabled === '1') {
      state.general.interval = {};
      state.general.interval.enabled = data.settings.general?.interval?.enabled === '1' ?? false;
      state.general.interval.time = data.settings.general?.interval?.time
        ? Number(data.settings.general?.interval?.time)
        : 6000;
      state.key = generateUniqueId();
    } else {
      state.general = {};
      state.general.interval = {};
      state.general.interval.enabled = false;
      state.general.interval.time = 6000;
      state.key = generateUniqueId();
    }
    if (data.settings.general?.transition_time) {
      state.general.transitionTime = Number(data.settings.general.transition_time);
    }

    if (data.settings.general?.navigation) {
      state.general.navigation = {};
      if (data.settings.general?.navigation?.arrow_buttons === '1') {
        state.general.navigation.arrowButtons = true;
      } else if (data.settings.general?.navigation?.arrow_buttons === '0') {
        state.general.navigation.arrowButtons = false;
      } else state.general.navigation.arrowButtons = !!data.settings.general?.navigation?.enabled;

      if (data.settings.general?.navigation?.bullets === '1') {
        state.general.navigation.bullets = true;
      } else if (data.settings.general?.navigation?.bullets === '0') {
        state.general.navigation.bullets = false;
      } else state.general.navigation.bullets = !!data.settings.general?.navigation?.enabled;
      state.general.navigation.enabled = data.settings.general?.navigation?.enabled === '1' ?? false;
    }

    state.general.type = data.settings.general?.type;
    if (data.settings.general?.images) {
      state.general.images = Number(data.settings.general?.images);
    } else {
      state.general.images = 0;
    }

    state.general.shuffle = data.settings.general?.shuffle === '1' ?? false;
    state.general.transition = data.settings.general?.transition;
    state.general.transitionTime = Number(data.settings.general?.transition_time);
    state.images = [];

    if (state.general?.shuffle) {
      if (data.settings.images) {
        data.settings.images = shuffle(data.settings.images);
      }
      if (state.images && state.images.length > 0) {
        state.images = shuffle(state.images);
      }
    }

    if (data.settings.images && data.settings.images.length > 0) {
      data.settings.images.forEach((image) => {
        if (image.image && image.image !== '') {
          state.images?.push({
            id: image.id,
            ...(image.link && {
              link: {
                enabled: image.link.enabled === '1',
                popover: image.link.popover,
                section: image.link.section,
                target: image.link.target,
                type: image.link.type,
                url: image.link.url
              }
            }),
            height: image.height,
            ...(image.image && image.image !== '' && { image: image.image }),
            width: image.width
          });
        }
      });
    }

    if ((state.images && state.images.length === 1) || state.general.images === 1) {
      state.general.interval.enabled = false;
      if (state.general && state.general.navigation) {
        state.general.navigation.enabled = false;
      }
    }

    setTimeout(() => {
      state.isReady = true;
    }, 100);
  }

  public isAddonValid(): boolean {
    const data = this.getData();
    return !(!data.settings.images || data.settings.images.length === 0);
  }

  authorSignature(): string {
    return 'Nicky Christensen';
  }
}
